// The Firestore query behind the approved Lao eTax documents page
// (views/company/documents.approved.vue).
//
// Why this exists: that page used to subscribe to `@LaoEtaxStatus ==
// 'approved'` with no bound at all — every approved document the company had
// ever issued, read in full on every page open, line items and all, to render
// eight fields of each. Its month and type filters ran client-side, so
// narrowing to October still paid for the whole year first, and the SPA sets
// no Firestore persistence, so nothing was cached between visits either. Same
// failure as the ThaiSmartTax lists before libs/docDateRange.js.
//
// So the month filter IS the query now. Reads per page open stop growing with
// the company's history.
//
// Unlike docDateRange, the window is bounded on BOTH sides: this page is a
// monthly register, not a working list. Which makes the default month a real
// decision, and the calendar month is the WRONG answer — a company whose last
// filing was in July opens on an empty October and reads as broken. So the
// page opens on the newest month that has documents; probeApprovedMonths finds
// it, and also keeps the calendar month selectable so someone mid-month can
// watch for today's filings. Future-dated documents stay reachable the same
// way: the list runs up to the newest document whenever that is later.
//
// ── Why ranging on ED.IssueDateTime is safe ───────────────────────────────
//
// Two properties, neither obvious, both load-bearing:
//
// 1. A Firestore orderBy silently DROPS documents that lack the field, and
//    dropping rows from a tax register is the one failure this page must not
//    have. libEtaxDocMapLao writes 'ED.IssueDateTime' unconditionally for
//    every Lao document — '' when the issue date will not parse — so there is
//    nothing to drop. '' sorts below every bound, which puts such a document
//    under "ทุกเดือน" alone, exactly where the page's old client-side
//    `issueDate.slice(0, 7)` filter put it.
//
// 2. The stored value is "YYYY-MM-DDTHH:mm:ss.SSS+07:00": fixed width, and
//    libEtaxDocMapLao stamps the same +07:00 on every document regardless of
//    where the time came from. Lexicographic order is therefore chronological
//    order, and a bare "YYYY-MM-DD" is a valid bound against the longer
//    stored string — "2026-10-31T23:59:59.999+07:00" < "2026-11-01".
//
// ── Indexes ───────────────────────────────────────────────────────────────
//
// Two composite indexes on the `Docs` collection are required, created BY
// HAND in the Firebase console (firestore.indexes.json is a stale snapshot —
// see the warning in README.md). Without them every query here fails with
// `failed-precondition`; describeQueryError turns that into something
// actionable rather than a stack trace.
//
//   `@LaoEtaxStatus` Asc + `ED.IssueDateTime` Asc    a month, and the probes
//   `@LaoEtaxStatus` Asc + `ED.IssueDateTime` Desc   "ทุกเดือน", and the probe
//
// Backticks because both are LITERAL top-level keys and ED.IssueDateTime
// contains dots — the same spelling the other Docs indexes use.

import firebase from "firebase/app";
import moment from "moment";

export const APPROVED = "approved";

// The value the month filter carries for "every month".
export const ALL_MONTHS = "all";

const STATUS_FIELD = "@LaoEtaxStatus";
const ISSUE_FIELD = "ED.IssueDateTime";
const MONTH_FORMAT = "YYYY-MM";
const DATE_FORMAT = "YYYY-MM-DD";

// The ceiling on "ทุกเดือน". Deliberately a number one month will not reach,
// so picking a month is never capped and the cap only ever appears on the one
// option that asks for the whole history. Callers MUST surface it when it
// bites: a silently truncated tax register is worse than a slow one.
export const ALL_MONTHS_LIMIT = 2000;

// A company with one bad ED.IssueDateTime (a 1970, a 2999) must not get a
// dropdown of a thousand months. Five years back is well past any real need.
export const MAX_MONTH_OPTIONS = 60;

// Both are literal top-level Firestore keys, and ED.IssueDateTime contains
// dots — which is exactly what a one-segment FieldPath means. Passing the
// plain string would make Firestore look for `IssueDateTime` inside a map
// called `ED` and match nothing. Same reasoning as store/collection/docs.js
// toFieldPath; nothing here touches the one real nested map (@DocData).
const fieldPath = (name) => new firebase.firestore.FieldPath(name);

export const thisMonth = () => moment().format(MONTH_FORMAT);

/** The half-open [from, to) string bounds of one "YYYY-MM". */
export function monthBounds(month) {
  const start = moment(month + "-01", DATE_FORMAT, true);
  return {
    from: start.format(DATE_FORMAT),
    to: start.clone().add(1, "month").format(DATE_FORMAT),
  };
}

const approvedDocs = (db, companyID) =>
  db
    .collection(`Companies/${companyID}/Docs`)
    .where(fieldPath(STATUS_FIELD), "==", APPROVED);

/**
 * The listener query for one month, or for ALL_MONTHS.
 *
 * A month needs no orderBy or limit: Firestore implicitly orders by the range
 * field, and one month of one company is a bounded set by construction.
 * ALL_MONTHS has no range to bound it, so the bound is a count — with an
 * explicit `desc` so the N we keep is the newest N. Without it Firestore would
 * order by document name and the cap would cut an arbitrary slice of history.
 */
export function buildApprovedQuery(db, companyID, month) {
  const base = approvedDocs(db, companyID);
  if (month === ALL_MONTHS) {
    return base.orderBy(fieldPath(ISSUE_FIELD), "desc").limit(ALL_MONTHS_LIMIT);
  }
  const bounds = monthBounds(month);
  return base
    .where(fieldPath(ISSUE_FIELD), ">=", bounds.from)
    .where(fieldPath(ISSUE_FIELD), "<", bounds.to);
}

function monthOf(snap) {
  if (!snap || snap.empty) return "";
  const value = (snap.docs[0].data() || {})[ISSUE_FIELD];
  const m = moment(value, moment.ISO_8601, true);
  return m.isValid() ? m.format(MONTH_FORMAT) : "";
}

/**
 * Which months the company has approved documents in.
 *
 * Two reads, once per page open: the oldest and the newest approved document
 * with a usable date.
 *
 * Returns `{ months, newest, oldest, failed }`:
 *
 *   months  every month from `newest` (or floorMonth, whichever is later)
 *           down to `oldest`, newest first — gaps included. An empty month in
 *           the list is a truthful answer ("nothing was approved then"), and
 *           the alternative is a per-month count this schema does not carry
 *           (the Firebase v7 SDK here has no count aggregation either).
 *   newest  the month to OPEN on. Not the same as floorMonth: a company whose
 *           last filing was in July must not be shown an empty October just
 *           because that is today — a register that opens blank reads as
 *           broken rather than as up to date.
 *   failed  the reads threw. Callers must say so rather than degrade quietly:
 *           the descending probe is the only thing here that needs the Desc
 *           index, so this is exactly what someone sees when they created one
 *           of the two indexes and not the other.
 *
 * floorMonth keeps the CURRENT month selectable even when nothing has been
 * approved in it yet, so someone mid-month can watch for today's filings.
 *
 * Both probes skip the '' that libEtaxDocMapLao writes for an unparseable
 * issue date. Without that the ascending one would return it — '' sorts first
 * — and the list would run from the epoch to today.
 */
export async function probeApprovedMonths(db, companyID, floorMonth) {
  const base = approvedDocs(db, companyID).where(fieldPath(ISSUE_FIELD), ">", "");
  // Caught per read, not around a Promise.all: the two need DIFFERENT indexes,
  // and the descending one is the index people create second. Letting one
  // rejection discard the other's answer would throw away the month list at
  // exactly the moment it is the user's only way to reach their documents.
  const [oldestSnap, newestSnap] = await Promise.all([
    base
      .orderBy(fieldPath(ISSUE_FIELD), "asc")
      .limit(1)
      .get()
      .catch(() => null),
    base
      .orderBy(fieldPath(ISSUE_FIELD), "desc")
      .limit(1)
      .get()
      .catch(() => null),
  ]);
  const failed = !oldestSnap || !newestSnap;
  const oldest = monthOf(oldestSnap);
  const newest = monthOf(newestSnap);
  if (!oldest) return { months: [], newest: "", oldest: "", failed };
  // Without `newest` there is no month to open on — but `oldest` alone still
  // fills the dropdown down to the first document, so the data stays reachable
  // by hand while the missing index is being created.
  //
  // A future-dated document is a data-entry slip, not something to hide: the
  // top of the list follows the newest document when it runs past the floor,
  // so its month stays reachable too.
  const top = newest && newest > floorMonth ? newest : floorMonth;
  const months = [];
  const cursor = moment(top + "-01", DATE_FORMAT);
  const first = moment(oldest + "-01", DATE_FORMAT);
  while (cursor.isSameOrAfter(first) && months.length < MAX_MONTH_OPTIONS) {
    months.push(cursor.format(MONTH_FORMAT));
    cursor.subtract(1, "month");
  }
  return { months, newest, oldest, failed };
}

/**
 * `failed-precondition` is how Firestore reports a missing composite index.
 * Its raw message does carry a one-click create link, but it is buried
 * mid-sentence, and the person reading the banner needs to know the fix is a
 * console action and not a bug in the page. The raw text is kept for the link.
 */
export function describeQueryError(err) {
  const raw = (err && err.message) || String(err);
  if (err && err.code === "failed-precondition") {
    return (
      "ยังไม่มี Firestore index ที่หน้านี้ต้องใช้ — สร้างบน collection Docs: " +
      "`@LaoEtaxStatus` (Asc) + `ED.IssueDateTime` (Asc และ Desc) " +
      "ในคอนโซล Firebase แล้วรีเฟรช / " +
      raw
    );
  }
  return raw;
}

export default {
  ALL_MONTHS,
  ALL_MONTHS_LIMIT,
  buildApprovedQuery,
  probeApprovedMonths,
  describeQueryError,
  monthBounds,
  thisMonth,
};
